import axios from 'axios'
import { resolveAuthToken } from '../utils/authToken'
import { isClerkEnabled } from '../config/clerkConfig'
import { emitGlobalToast } from '../utils/globalToast'

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3002/api',
  timeout: 30000, // 30 segundos para operaciones que recalculan datos
  headers: {
    'Content-Type': 'application/json',
  },
})

// --- Modo DEMO (landing): sandbox de escrituras por pestaña ---
// Bajo /demo (window.__CD_DEMO__, lo activa DemoSessionBoot) las mutaciones
// NO viajan al server: se responden sintéticas con éxito y eco del payload.
// Los reducers .fulfilled de los slices aplican el cambio solo en el Redux de
// esta pestaña = "memoria en caché" del visitante. El server además tiene su
// propio candado 403 por si algo se escapa.
let demoSeq = 0
const demoAdapter = async (config) => {
  let body = {}
  try {
    body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data || {}
  } catch {
    body = {}
  }
  const eco = { id: `demo-${Date.now()}-${++demoSeq}`, ...body, demo: true }
  return {
    data: { success: true, demo: true, message: 'Guardado (modo demo — no persiste)', data: eco, ...eco },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: {},
  }
}

// Interceptor para agregar token a las requests
api.interceptors.request.use(
  (config) => {
    // resolveAuthToken prioriza Clerk (si está enabled) y cae a legacy localStorage
    const token = resolveAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // FormData (uploads: PDF de circulares, fotos de bombero, documentos de licencia…):
    // esta instancia tiene `Content-Type: application/json` por defecto, y axios 1.x, al ver ese
    // header, JSON-encodea el FormData (el File queda como `{}`) en vez de mandar multipart →
    // el server recibía `{"archivo":{}}` y respondía «"archivo" is not allowed», perdiendo el
    // archivo. Borrando el header, axios arma el multipart con boundary y el archivo viaja bien.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    if (
      window.__CD_DEMO__ &&
      !['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())
    ) {
      config.adapter = demoAdapter
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => {
    // No envolver Blob responses — se usan para export PDF/Word/Excel
    if (response.data instanceof Blob) {
      return response
    }
    // Retornar solo los datos serializables
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    }
  },
  (error) => {
    // En demo, un 401 (token demo expirado) recarga para pedir sesión nueva
    if (window.__CD_DEMO__ && error.response?.status === 401) {
      window.location.reload()
      return Promise.reject(error)
    }
    // Si el token expira o es inválido (401), limpiar sesión y redirigir al login
    if (error.response?.status === 401) {
      // Si Clerk está activo, su SDK gestiona la sesión: solo limpiamos restos legacy
      // y redirigimos a /sign-in. Si no, comportamiento clásico hacia /login.
      const clerkOn = isClerkEnabled()

      localStorage.removeItem('bomberosToken')
      localStorage.removeItem('bomberosUser')
      localStorage.removeItem('bomberosTenant')

      // Determinar destino según modo
      const pathParts = window.location.pathname.split('/').filter(Boolean)
      const slug = pathParts.length > 0 ? pathParts[0] : null
      const loginPath = clerkOn
        ? '/sign-in'
        : slug
        ? `/${slug}/login`
        : '/login'

      // No redirigir desde rutas públicas / de auth para evitar loops
      const path = window.location.pathname
      const isPublic =
        path === '/' ||
        path === '/login' ||
        path === '/registro' ||
        path === '/confirmar-correo' ||
        path === '/recuperar-acceso' ||
        path.startsWith('/registro/') ||
        path.startsWith('/sign-in') ||
        path.startsWith('/sign-up') ||
        path.endsWith('/login') ||
        path.startsWith('/invitacion/')
      if (!isPublic) {
        window.location.href = loginPath
      }
    }

    // Errores de red / 5xx que hoy fallan en silencio (el warmup deja skeletons sin
    // explicación). Toast global con dedupe. 401 ya se maneja arriba; 4xx son errores
    // esperados que cada caller muestra → no se tocan acá.
    const status = error.response?.status
    if (!error.response && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED') {
      emitGlobalToast('error', 'Sin conexión con el servidor. Revisá tu red e intentá de nuevo.')
    } else if (status >= 500) {
      emitGlobalToast('error', 'Hubo un error en el servidor. Intentá de nuevo en un momento.')
    }

    return Promise.reject(error)
  }
)

// Servicios de API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  // refreshToken/getProfile eliminados: los endpoints /auth/refresh y
  // /auth/profile no existen en el server y nadie los llamaba.
}

export const bomberosAPI = {
  getAll: () => api.get('/bomberos'),
  getById: (id) => api.get(`/bomberos/${id}`),
  create: (data) => api.post('/bomberos', data),
  update: (id, data) => api.put(`/bomberos/${id}`, data),
  delete: (id) => api.delete(`/bomberos/${id}`),
}

export const citacionesAPI = {
  getAll: () => api.get('/citaciones'),
  getById: (id) => api.get(`/citaciones/${id}`),
  create: (data) => api.post('/citaciones', data),
  update: (id, data) => api.put(`/citaciones/${id}`, data),
  delete: (id) => api.delete(`/citaciones/${id}`),
  getAsistenciaBomberos: (params) => api.get('/citaciones/stats/asistencia-bomberos', { params }),
}

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  // createUser/deleteUser/getLogs eliminados: esos endpoints nunca existieron
  // en el server (POST/DELETE /admin/users, GET /admin/logs) y nadie los usaba.
}

export const logsAPI = {
  getSesiones: (params) => api.get('/logs/sesiones', { params }),
}

export const permisosAPI = {
  getAll: () => api.get('/permisos'),
  getByCargo: (cargoId) => api.get(`/permisos/cargos/${cargoId}`),
  updateCargo: (cargoId, permisoIds) => api.put(`/permisos/cargos/${cargoId}`, { permisoIds }),
}

export const guardiasAPI = {
  getMensuales: (params) => api.get('/guardias/mensuales', { params }),
  getMensualById: (id) => api.get(`/guardias/mensuales/${id}`),
  createMensual: (data) => api.post('/guardias/mensuales', data),
  updateMensual: (id, data) => api.put(`/guardias/mensuales/${id}`, data),
  deleteMensual: (id) => api.delete(`/guardias/mensuales/${id}`),
  saveDia: (guardiaId, data) => api.post(`/guardias/mensuales/${guardiaId}/dias`, data),
  deleteDia: (diaId) => api.delete(`/guardias/dias/${diaId}`),
  getBomberos: (params) => api.get('/guardias/bomberos', { params }),
  exportPdf: (id) => api.get(`/guardias/mensuales/${id}/export/pdf`, { responseType: 'blob' }),
  
  // Plantillas
  getPlantillas: () => api.get('/guardias/plantillas'),
  getPlantillaById: (id) => api.get(`/guardias/plantillas/${id}`),
  createPlantilla: (data) => api.post('/guardias/plantillas', data),
  updatePlantilla: (id, data) => api.put(`/guardias/plantillas/${id}`, data),
  deletePlantilla: (id) => api.delete(`/guardias/plantillas/${id}`),
  aplicarPlantilla: (guardiaId, plantillaId) => api.post(`/guardias/mensuales/${guardiaId}/aplicar-plantilla/${plantillaId}`),
  aplicarRolSemanal: (guardiaId, asignaciones) => api.post(`/guardias/mensuales/${guardiaId}/aplicar-rol-semanal`, { asignaciones }),
  
  // Nuevas funcionalidades
  verificarLicencias: (data) => api.post('/guardias/verificar-licencias', data),
  getEstadisticas: (params) => api.get('/guardias/estadisticas', { params }),
  getResumenAnual: (params) => api.get('/guardias/resumen-anual', { params }),

  // Intercambios
  getIntercambios: (params) => api.get('/guardias/intercambios', { params }),
  crearIntercambio: (data) => api.post('/guardias/intercambios', data),
  responderIntercambio: (id, data) => api.put(`/guardias/intercambios/${id}/responder`, data),
  aprobarIntercambio: (id, data) => api.put(`/guardias/intercambios/${id}/aprobar`, data),
  cancelarIntercambio: (id) => api.delete(`/guardias/intercambios/${id}`),

  // Reemplazo directo (admin) + historial de cambios al rol
  reemplazoDirecto: (asignacionId, data) => api.post(`/guardias/dias-bombero/${asignacionId}/reemplazar`, data),
  getHistorial: (guardiaId, params) => api.get(`/guardias/mensuales/${guardiaId}/historial`, { params }),

  // Horario configurable
  getConfiguracion: () => api.get('/guardias/configuracion'),
  updateConfiguracion: (data) => api.put('/guardias/configuracion', data),

  // Resumen de guardia
  getResumen: (guardiaDiaId) => api.get(`/guardias/dias/${guardiaDiaId}/resumen`),
  exportResumen: (guardiaDiaId, formato) => api.get(`/guardias/dias/${guardiaDiaId}/resumen/export/${formato}`, { responseType: 'blob' }),
  generarAsistencia: (guardiaDiaId) => api.post(`/guardias/dias/${guardiaDiaId}/resumen/generar-asistencia`),
  updateAsistencia: (id, data) => api.put(`/guardias/asistencia/${id}`, data),
}

export const emergenciasAPI = {
  list: (params) => api.get('/emergencias', { params }),
  get: (id) => api.get(`/emergencias/${id}`),
  create: (data) => api.post('/emergencias', data),
  update: (id, data) => api.put(`/emergencias/${id}`, data),
  remove: (id) => api.delete(`/emergencias/${id}`),
}

export const libroGuardiaAPI = {
  get: (guardiaDiaId) => api.get(`/libro-guardia/${guardiaDiaId}`),
  abrir: (guardiaDiaId, data) => api.post(`/libro-guardia/${guardiaDiaId}/abrir`, data || {}),
  agregarEntrada: (libroId, data) => api.post(`/libro-guardia/${libroId}/entradas`, data),
  cerrar: (libroId, data) => api.put(`/libro-guardia/${libroId}/cerrar`, data || {}),
  eliminarEntrada: (entradaId) => api.delete(`/libro-guardia/entradas/${entradaId}`),
  getRecientes: (params) => api.get('/libro-guardia', { params }),
}

export const estadisticasAPI = {
  getDisponibilidad: (params) => api.get('/estadisticas/disponibilidad', { params }),
  getDisponibilidadMensual: (params) => api.get('/estadisticas/disponibilidad-mensual', { params }),
  getLicenciasResumen: (params) => api.get('/estadisticas/licencias-resumen', { params }),
  getTendencias: () => api.get('/estadisticas/tendencias'),
}

export default api
